import { useI18n as internalUseI18n, type VueMessageType } from 'vue-i18n'

export function useI18n(baseKey?: string) {
  const { t: internalT, te: internalTe, tm: internalTm, ...vueI18n } = internalUseI18n()

  const parentBaseKey = inject($I18nBaseKey, toRef(''))

  const currentBaseKey = computed(() => {
    if (baseKey) {
      if (baseKey.startsWith('.') && parentBaseKey.value) {
        return `${parentBaseKey.value}${baseKey}`
      }
      return baseKey
    }
    return parentBaseKey.value
  })

  // Only provide a computed base key if one was specified
  if (baseKey) {
    provide($I18nBaseKey, currentBaseKey)
  }

  const computeKey = (key: string) => {
    if (!key.startsWith('.')) {
      return key
    }

    return `${currentBaseKey.value}${key}`
  }

  // Can't use a shared function for these due to types -_-
  const t = new Proxy(internalT, {
    apply(target, thisArg, [key, ...rest]) {
      const computedKey = typeof key === 'string' ? computeKey(key) : key
      return Reflect.apply(target, thisArg, [computedKey, ...rest])
    }
  })

  const te = new Proxy(internalTe, {
    apply(target, thisArg, [key, ...rest]) {
      const computedKey = typeof key === 'string' ? computeKey(key) : key
      return Reflect.apply(target, thisArg, [computedKey, ...rest])
    }
  })

  const isVueI18nMessageAst = (value: unknown): boolean => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false
    }

    return 'body' in value && 'start' in value && 'end' in value && 'loc' in value
  }

  const resolveTranslation = (
    item: VueMessageType,
    bindings: Record<string, unknown> | undefined = undefined
  ): any => {
    if (!item) {
      return item
    } else if (isVueI18nMessageAst(item)) {
      return vueI18n.rt(item, bindings ?? {})
    } else if (Array.isArray(item)) {
      return item.map((item) => resolveTranslation(item, bindings))
    } else if (typeof item === 'object') {
      const mappedObject = Object.entries(item).map(([key, value]) => {
        return [key, resolveTranslation(value, bindings)]
      })
      return Object.fromEntries(mappedObject)
    } else {
      return vueI18n.rt(item, bindings ?? {})
    }
  }

  // Run `tm` without resolving the translation with `rt` afterwards
  const baseTm = new Proxy(internalTm, {
    apply(target, thisArg, [key, ...rest]): VueMessageType {
      const computedKey = typeof key === 'string' ? computeKey(key) : key
      return Reflect.apply(target as any, thisArg, [computedKey, ...rest])
    }
  })

  function tmWithBindings(
    key: string,
    bindings?: Record<string, string>
  ): ReturnType<typeof internalTm> {
    return resolveTranslation(baseTm(key), bindings)
  }

  // Run `tm` and `rt` over all the resulting items
  const tm = new Proxy(internalTm, {
    apply(_target, _thisArg, [key]) {
      return tmWithBindings(key)
    }
  })

  return {
    ...vueI18n,
    t,
    te,
    tm,
    baseTm,
    tmWithBindings,
    computeKey,
    baseKey: currentBaseKey.value,

    isVueI18nMessageAst
  }
}
